import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { Configuration } from '@/types';
import { useMutation } from '@/utils/api';

export function useGetConfiguration(options?: {
  disableCache?: boolean;
  email?: string;
}) {
  return useMy(
    { resource: 'configuration' },
    {
      ...(options?.disableCache
        ? {
            staleTime: 0, // Data is immediately stale
            gcTime: 0, // Don't cache the data
            refetchOnMount: 'always' as const, // Always refetch when component mounts
            refetchOnWindowFocus: true, // Refetch when window gains focus
          }
        : {}),
      email: options?.email,
    }
  );
}

export function useUpdateConfiguration(options?: { email?: string }) {
  const axios = useAxios({
    email: options?.email,
    doNotImpersonate: !!options?.email,
  });

  return useMutation<void, Error, Configuration>({
    defaultErrorMessage: 'Failed to update configuration',
    mutationFn: async (configuration: Configuration) => {
      await axios.put('/configuration', configuration);

      // Invalidate configuration cache (with and without email for proper refresh)
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('configuration'),
      });

      // If updating a specific customer, also invalidate their specific cache
      if (options?.email) {
        queryClient.invalidateQueries({
          queryKey: [...getQueryKey.getMy('configuration'), options.email],
        });
      }

      // EVM config changes trigger backend PAS recalculation — refresh statistics
      if (configuration.name.startsWith('evm#')) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy('statistic'),
        });
      }
    },
  });
}

export function useDeleteConfiguration(options?: { email?: string }) {
  const axios = useAxios();
  const { getToken } = useAuth();

  return useMutation<void, Error, { name: string }>({
    defaultErrorMessage: 'Failed to delete configuration',
    mutationFn: async ({ name }: { name: string }) => {
      if (options?.email) {
        const token = await getToken();
        await axios.delete('/configuration', {
          headers: {
            Authorization: `Bearer ${token}`,
            account: options.email,
          },
          data: { name },
        });
      } else {
        await axios.delete('/configuration', {
          data: { name },
        });
      }

      // Invalidate configuration cache (with and without email for proper refresh)
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('configuration'),
      });

      // If deleting a specific customer's configuration, also invalidate their specific cache
      if (options?.email) {
        queryClient.invalidateQueries({
          queryKey: [...getQueryKey.getMy('configuration'), options.email],
        });
      }

      // EVM config changes trigger backend PAS recalculation — refresh statistics
      if (name.startsWith('evm#')) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy('statistic'),
        });
      }
    },
  });
}
